/**
 * `localStorage` that cannot throw.
 *
 * Reading `window.localStorage` is not a plain property access in every
 * browser: it is a getter that raises `SecurityError` when storage is blocked
 * for the origin — Safari/WebKit with "Block all cookies", and any embed in a
 * sandboxed iframe without `allow-same-origin`. `setItem` has its own failure
 * (`QuotaExceededError`, which Safari's private mode raises on the first
 * write). Both are guarded here rather than at the call sites, because the
 * call sites are `useState` initialisers that run *during render*: a throw
 * there takes the tree down, and neither SPA has an error boundary, so the
 * whole page goes blank. `ThemeToggle` is mounted on the public pages too, so
 * that would take sign-in with it.
 *
 * The fallback is in-memory and module-scoped, so a preference set in a
 * storage-blocked browser still holds for the rest of the session and is
 * simply forgotten on reload. That is the right degradation: the feature that
 * depends on storage stops persisting, and nothing else notices.
 */
export type SafeStorage = Pick<Storage, 'getItem' | 'setItem'>

/** Read once to prove the store *answers*, rather than merely that it exists. */
const PROBE_KEY = 'augrank.storage-probe'

/** Shared by every `safeStorage()` caller, so writes read back within a session. */
const memory = new Map<string, string>()

const memoryStorage: SafeStorage = {
  getItem: (key) => memory.get(key) ?? null,
  setItem: (key, value) => {
    memory.set(key, value)
  },
}

export function safeStorage(): SafeStorage {
  let backing: Storage
  try {
    backing = window.localStorage
    // Touching it is the probe: some browsers hand back the object and only
    // raise on use, so a bare property read is not proof that it works.
    backing.getItem(PROBE_KEY)
  } catch {
    return memoryStorage
  }
  return {
    getItem: (key) => {
      let stored: string | null
      try {
        stored = backing.getItem(key)
      } catch {
        return memoryStorage.getItem(key)
      }
      // Falling through to memory on a miss, not only on a throw: a write that
      // hit the quota below landed in memory, and a read that ignored it would
      // hand back the old value the user has just changed.
      return stored ?? memoryStorage.getItem(key)
    },
    setItem: (key, value) => {
      try {
        backing.setItem(key, value)
      } catch {
        // Quota, or storage revoked mid-session. Keep the value for this
        // session rather than dropping the user's choice on the floor.
        memoryStorage.setItem(key, value)
      }
    },
  }
}
